import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';

/**
 * Signature hero backdrop: a slow-rotating mechanical "forge wheel"
 * rendered as pure SVG (no WebGL) for perfect React 19 compatibility.
 * Two counter-rotating rings, tick marks, spokes, and a soft parallax
 * driven by the mouse.
 */
export default function HeroCanvas() {
  const layerRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const el = layerRef.current;
      if (!el) return;
      const { innerWidth: w, innerHeight: h } = window;
      const x = (e.clientX / w - 0.5) * 2;
      const y = (e.clientY / h - 0.5) * 2;
      el.style.transform = `translate3d(${x * -18}px, ${y * -14}px, 0)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const tickCount = 60;
  const spokeCount = 24;

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
      {/* Ambient background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 55%, rgba(255,90,31,0.12), transparent 45%), radial-gradient(circle at 50% 100%, rgba(0,0,0,0.85), transparent 55%), #050403',
        }}
      />

      {/* Wheel layer (mouse-parallax) */}
      <div
        ref={layerRef}
        className="absolute inset-0 flex items-center justify-center transition-transform duration-500 ease-out will-change-transform"
      >
        <motion.svg
          viewBox="-320 -320 640 640"
          className="w-[92vmin] h-[92vmin] max-w-[900px] max-h-[900px] opacity-90"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 0.9, scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Faint mid ring */}
          <circle cx="0" cy="0" r="200" fill="none" stroke="rgba(244,239,230,0.09)" strokeWidth="1" />
          <circle cx="0" cy="0" r="120" fill="none" stroke="rgba(244,239,230,0.05)" strokeWidth="1" />

          {/* Outer ring (rotates slowly clockwise) */}
          <g style={{ transformOrigin: '0 0', animation: 'spin-slow 90s linear infinite' }}>
            <circle cx="0" cy="0" r="280" fill="none" stroke="#ff5a1f" strokeWidth="1.2" opacity="0.9" />
            {/* Tick marks */}
            {new Array(tickCount).fill(0).map((_, i) => {
              const angle = (i / tickCount) * Math.PI * 2;
              const x1 = Math.cos(angle) * 275;
              const y1 = Math.sin(angle) * 275;
              const isMajor = i % 5 === 0;
              const x2 = Math.cos(angle) * (isMajor ? 258 : 268);
              const y2 = Math.sin(angle) * (isMajor ? 258 : 268);
              return (
                <line
                  key={`t${i}`}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={isMajor ? '#ff5a1f' : '#f4efe6'}
                  strokeWidth={isMajor ? 1.4 : 0.6}
                  opacity={isMajor ? 0.95 : 0.28}
                />
              );
            })}
            {/* Compass labels */}
            {['N', 'E', 'S', 'W'].map((L, i) => {
              const angle = (i / 4) * Math.PI * 2 - Math.PI / 2;
              const x = Math.cos(angle) * 300;
              const y = Math.sin(angle) * 300;
              return (
                <text
                  key={L}
                  x={x} y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#ff5a1f"
                  fontFamily="JetBrains Mono, monospace"
                  fontSize="10"
                  letterSpacing="0.3em"
                >
                  {L}
                </text>
              );
            })}
          </g>

          {/* Middle ring counter-rotating */}
          <g style={{ transformOrigin: '0 0', animation: 'spin-slow-rev 60s linear infinite' }}>
            <circle cx="0" cy="0" r="170" fill="none" stroke="rgba(244,239,230,0.35)" strokeWidth="0.8" />
            {new Array(spokeCount).fill(0).map((_, i) => {
              const angle = (i / spokeCount) * Math.PI * 2;
              const x1 = Math.cos(angle) * 88;
              const y1 = Math.sin(angle) * 88;
              const x2 = Math.cos(angle) * 170;
              const y2 = Math.sin(angle) * 170;
              const isMajor = i % 6 === 0;
              return (
                <line
                  key={`sp${i}`}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="#f4efe6"
                  strokeWidth={isMajor ? 0.9 : 0.4}
                  opacity={isMajor ? 0.55 : 0.14}
                />
              );
            })}
          </g>

          {/* Inner hub */}
          <g style={{ transformOrigin: '0 0', animation: 'spin-slow 120s linear infinite reverse' }}>
            <circle cx="0" cy="0" r="60" fill="none" stroke="#ff5a1f" strokeWidth="0.9" opacity="0.55" />
            <circle cx="0" cy="0" r="40" fill="none" stroke="rgba(244,239,230,0.35)" strokeWidth="0.6" />
            <circle cx="0" cy="0" r="4" fill="#ff5a1f" />
          </g>

          {/* Crosshair */}
          <line x1="-320" y1="0" x2="-290" y2="0" stroke="rgba(255,90,31,0.35)" strokeWidth="0.8" />
          <line x1="290" y1="0" x2="320" y2="0" stroke="rgba(255,90,31,0.35)" strokeWidth="0.8" />
          <line x1="0" y1="-320" x2="0" y2="-290" stroke="rgba(255,90,31,0.35)" strokeWidth="0.8" />
          <line x1="0" y1="290" x2="0" y2="320" stroke="rgba(255,90,31,0.35)" strokeWidth="0.8" />
        </motion.svg>
      </div>

      {/* Foreground vignette on top of graphic */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 55%, transparent 30%, rgba(5,4,3,0.65) 85%)',
        }}
      />

      <style>{`
        @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes spin-slow-rev { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      `}</style>
    </div>
  );
}
